/**
 * FAQ Group accordion.
 *
 * Drives the FAQ accordion group. Each header is a native <button> wrapped in a
 * heading, exposing the question text followed by the button role, the
 * expanded/collapsed state and the heading level to assistive technologies.
 *
 * Click behavior is bound to the accordion buttons only. The <faq-group-accordion>
 * container is intentionally non-interactive so assistive technologies do not
 * expose it as "clickable".
 */
class FaqGroupAccordion extends HTMLElement {
  /** @type {HTMLElement[]} */
  #toggles = [];

  connectedCallback() {
    this.#toggles = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.faq-group__toggle')));
    this.#toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.#handleClick);
    });
  }

  disconnectedCallback() {
    this.#toggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.#handleClick);
    });
    this.#toggles = [];
  }

  /**
   * Toggles the FAQ panel associated with the clicked header button.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleClick = (event) => {
    const toggle = event.currentTarget;
    if (!(toggle instanceof HTMLElement)) return;

    const panelId = toggle.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    toggle.closest('.faq-group__item')?.classList.toggle('faq-group__item--open', !isExpanded);
  };
}

if (!customElements.get('faq-group-accordion')) {
  customElements.define('faq-group-accordion', FaqGroupAccordion);
}
